import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { supabase } from '../lib/supabase'
import { identifyUser, resetAnalytics, trackEvent } from '../lib/analytics'
import { useUserProfileStore } from './userProfile'
import type { User, Session } from '@supabase/supabase-js'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const session = ref<Session | null>(null)
  const loading = ref(true)

  const isAuthenticated = computed(() => !!session.value)

  async function init() {
    loading.value = true

    const { data } = await supabase.auth.getSession()
    session.value = data.session
    user.value = data.session?.user ?? null

    if (data.session?.user) {
      identifyUser(data.session.user.id, data.session.user.email)
      await useUserProfileStore().fetchProfile()
    }

    supabase.auth.onAuthStateChange(async (_event, s) => {
      session.value = s
      user.value = s?.user ?? null
      if (s?.user) {
        useUserProfileStore().fetchProfile()
      }
    })

    loading.value = false
  }

  async function signIn(email: string, password: string) {
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) throw error
    trackEvent('login')
  }

  async function signUp(email: string, password: string) {
    const { error } = await supabase.auth.signUp({ email, password })
    if (error) throw error
    trackEvent('signup')
  }

  async function signOut() {
    trackEvent('signout')
    await supabase.auth.signOut()
    user.value = null
    session.value = null
    useUserProfileStore().$reset()
    localStorage.removeItem('transfer-optimizer-recent-destinations')
    localStorage.removeItem('transfer-optimizer-presets')
    resetAnalytics()
  }

  return { user, session, loading, isAuthenticated, init, signIn, signUp, signOut }
})
